import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dimmer, Icon, Input, Loader, Message } from 'semantic-ui-react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getBuscaSKUAction } from '../../../actions/EspacoTecaActions';

const Page = styled.div`
  padding: 18px 0 26px;
  color: #152235;
`;

const SearchBar = styled.form`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0 24px;
  flex-wrap: wrap;
`;

const Label = styled.label`
  color: #222;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`;

const StyledInput = styled(Input)`
  &&& {
    width: 245px;
  }

  &&& input {
    height: 50px;
    font-size: 16px;
    font-weight: 600;
    color: #2f3a4a;
    border-color: #d8dee8;
    border-radius: 4px;
  }

  &&& input:focus {
    border-color: #0785d8;
    box-shadow: 0 0 0 1px #0785d8;
  }

  &&& input::placeholder {
    color: #c8ced8;
  }
`;

const SearchButton = styled(Button)`
  &&& {
    width: 155px;
    height: 50px;
    margin: 0;
    background: #0785d8;
    color: #fff;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 700;
  }

  &&&:hover,
  &&&:focus {
    background: #006fba;
    color: #fff;
  }
`;

const Shell = styled.div`
  position: relative;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(220px, 0.6fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryTile = styled.div`
  position: relative;
  min-height: 86px;
  padding: 16px 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: #2dd4bf;
  }
`;

const SummaryLabel = styled.div`
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const SummaryValue = styled.div`
  margin-top: 7px;
  color: #0f172a;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.25;
`;

const Section = styled.section`
  margin-top: 14px;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  min-height: 34px;
  padding: 8px 12px;
  background: #334155;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
`;

const ProductPath = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, auto) auto minmax(120px, auto) auto minmax(120px, auto);
  gap: 8px;
  align-items: center;
  color: #2dd4bf;
  font-size: 13px;
  font-weight: 700;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    width: 100%;
  }
`;

const ProductPathSeparator = styled.span`
  color: #2dd4bf;
  font-size: 16px;
  font-weight: 900;
  line-height: 1;
  opacity: 0.75;

  @media (max-width: 760px) {
    display: none;
  }
`;

const ProductPathItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
  text-align: center;
`;

const ProductPathLabel = styled.span`
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
`;

const ProductPathValue = styled.span`
  color: #2dd4bf;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.15;
  overflow-wrap: anywhere;
`;

const TableScroll = styled.div`
  max-height: 290px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 12px;
  background: #f3f6fa;
  color: #475569;
  font-weight: 800;
  text-align: left;
  border-bottom: 1px solid #dfe6ef;
`;

const Td = styled.td`
  padding: 9px 12px;
  color: #1f2937;
  border-bottom: 1px solid #edf1f7;
  vertical-align: top;
`;

const Tr = styled.tr`
  background: ${(props) => (props.$index % 2 === 0 ? '#fff' : '#f8fafc')};
`;

const HtmlTabs = styled.div`
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 10px 0;
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
  overflow-x: auto;
`;

const HtmlTab = styled.button`
  min-width: 160px;
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 7px 7px 0 0;
  border-bottom: 2px solid ${(props) => (props.$active ? '#c40000' : 'transparent')};
  background: ${(props) => (props.$active ? '#c7c7c7' : '#fff')};
  color: #000;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${(props) => (props.$active ? '#c7c7c7' : '#f4f4f4')};
  }
`;

const HtmlPreview = styled.div`
  height: 520px;
  overflow: auto;
  padding: 14px;
  background: #fff;
`;

const EmptyState = styled.div`
  padding: 22px;
  color: #64748b;
  font-size: 13px;
`;

const getEmptyData = () => ({
  infoProduto: [],
  atributos: [],
  conteudoSecao: [],
  html: []
});

function BuscaPorSKU({ buscaSku, buscaSkuLastSku, buscaSkuLoading, getBuscaSKUAction }) {
  const [sku, setSku] = useState(buscaSkuLastSku || '');
  const [skuBuscado, setSkuBuscado] = useState(buscaSkuLastSku || '');
  const [erro, setErro] = useState('');
  const [htmlAtivo, setHtmlAtivo] = useState('htmldescricao');

  const dados = buscaSku || getEmptyData();
  const infoProduto = dados.infoProduto && dados.infoProduto.length > 0 ? dados.infoProduto[0] : {};
  const atributos = dados.atributos || [];
  const conteudoSecao = dados.conteudoSecao || [];
  const html = dados.html && dados.html.length > 0 ? dados.html[0] : {};

  const htmlTabs = useMemo(() => [
    { key: 'htmldescricao', label: 'Informações do produto' },
    { key: 'htmlacessorios', label: 'Acessórios e Combinações' },
    { key: 'htmlinstalacao', label: 'Instalação' },
    { key: 'htmlmanutencao', label: 'Manutenção e Limpeza' }
  ], []);

  const htmlSelecionado = html[htmlAtivo] || '';
  const skuExibido = skuBuscado || buscaSkuLastSku;
  const temResultado = Boolean(
    infoProduto.nomeproduto ||
    atributos.length ||
    conteudoSecao.length ||
    htmlSelecionado
  );

  const handleBuscar = (event) => {
    event.preventDefault();

    const skuNormalizado = sku.trim();

    if (!skuNormalizado) {
      setErro('Informe o SKU.');
      setSkuBuscado('');
      return;
    }

    setErro('');
    setSkuBuscado(skuNormalizado);
    getBuscaSKUAction(skuNormalizado).catch(() => {
      setErro('Erro ao buscar SKU.');
    });
  };

  return (
    <Page>
      <SearchBar onSubmit={handleBuscar}>
        <Label htmlFor="busca-sku-input">SKU:</Label>
        <StyledInput
          id="busca-sku-input"
          value={sku}
          onChange={(event) => setSku(event.target.value)}
          placeholder="SKU"
        />
        <SearchButton type="submit" loading={buscaSkuLoading}>
          Buscar <Icon name="search" />
        </SearchButton>
      </SearchBar>

      {erro && (
        <Message negative size="small">
          {erro}
        </Message>
      )}

      <Shell>
        <Dimmer active={buscaSkuLoading} inverted>
          <Loader>Carregando</Loader>
        </Dimmer>

        {!skuExibido && (
          <Message size="small">
            Nenhum SKU buscado.
          </Message>
        )}

        {skuExibido && !buscaSkuLoading && !temResultado && (
          <Message info size="small">
            Nenhum resultado para o SKU {skuExibido}.
          </Message>
        )}

        {temResultado && (
          <>
            <SummaryGrid>
              <SummaryTile>
                <SummaryLabel>Nome</SummaryLabel>
                <SummaryValue>{infoProduto.nomeproduto || '-'}</SummaryValue>
              </SummaryTile>
              <SummaryTile>
                <SummaryLabel>SKU</SummaryLabel>
                <SummaryValue>{skuExibido}</SummaryValue>
              </SummaryTile>
            </SummaryGrid>

            <Section>
              <SectionTitle>
                <span>Produto:</span>
                <ProductPath>
                  <ProductPathItem>
                    <ProductPathLabel>Fabricante</ProductPathLabel>
                    <ProductPathValue>{infoProduto.fabricante || '-'}</ProductPathValue>
                  </ProductPathItem>
                  <ProductPathSeparator>&gt;</ProductPathSeparator>
                  <ProductPathItem>
                    <ProductPathLabel>Marca</ProductPathLabel>
                    <ProductPathValue>{infoProduto.marca || '-'}</ProductPathValue>
                  </ProductPathItem>
                  <ProductPathSeparator>&gt;</ProductPathSeparator>
                  <ProductPathItem>
                    <ProductPathLabel>Linha</ProductPathLabel>
                    <ProductPathValue>{infoProduto.linha || '-'}</ProductPathValue>
                  </ProductPathItem>
                </ProductPath>
              </SectionTitle>
            </Section>

            <Section>
              <SectionTitle>Atributos</SectionTitle>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <Th>ID Atributo</Th>
                      <Th>ID Valor</Th>
                      <Th>Atributo</Th>
                      <Th>Valor</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {atributos.map((item, index) => (
                      <Tr key={`${item.tecaatributoid}-${item.tecaatributovalorid}-${index}`} $index={index}>
                        <Td>{item.tecaatributoid}</Td>
                        <Td>{item.tecaatributovalorid}</Td>
                        <Td>{item.tecaatributo}</Td>
                        <Td><strong>{item.tecaatributovalor}</strong></Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                {atributos.length === 0 && <EmptyState>Nenhum atributo encontrado.</EmptyState>}
              </TableScroll>
            </Section>

            <Section>
              <SectionTitle>Conteudos</SectionTitle>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <Th>TecaconteudoID</Th>
                      <Th>Secao</Th>
                      <Th>Referencia</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {conteudoSecao.map((item, index) => (
                      <Tr key={`${item.tecaconteudoid}-${index}`} $index={index}>
                        <Td>{item.tecaconteudoid}</Td>
                        <Td>{item.tecasecao}</Td>
                        <Td>{item.referencia}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                {conteudoSecao.length === 0 && <EmptyState>Nenhum conteudo encontrado.</EmptyState>}
              </TableScroll>
            </Section>

            <Section>
              <SectionTitle>Exibicao HTML</SectionTitle>
              <HtmlTabs>
                {htmlTabs.map((tab) => (
                  <HtmlTab
                    key={tab.key}
                    type="button"
                    $active={htmlAtivo === tab.key}
                    onClick={() => setHtmlAtivo(tab.key)}
                  >
                    {tab.label}
                  </HtmlTab>
                ))}
              </HtmlTabs>
              <HtmlPreview>
                {htmlSelecionado ? (
                  <div dangerouslySetInnerHTML={{ __html: htmlSelecionado }} />
                ) : (
                  <EmptyState>Nenhum HTML encontrado.</EmptyState>
                )}
              </HtmlPreview>
            </Section>
          </>
        )}
      </Shell>
    </Page>
  );
}

BuscaPorSKU.defaultProps = {
  buscaSku: getEmptyData(),
  buscaSkuLastSku: '',
  buscaSkuLoading: false
};

BuscaPorSKU.propTypes = {
  buscaSku: PropTypes.shape({
    infoProduto: PropTypes.array,
    atributos: PropTypes.array,
    conteudoSecao: PropTypes.array,
    html: PropTypes.array
  }),
  buscaSkuLastSku: PropTypes.string,
  buscaSkuLoading: PropTypes.bool,
  getBuscaSKUAction: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  buscaSku: state.EspacoTecaReducer.buscaSku,
  buscaSkuLastSku: state.EspacoTecaReducer.buscaSkuLastSku,
  buscaSkuLoading: state.EspacoTecaReducer.buscaSkuLoading
});

export default connect(mapStateToProps, {
  getBuscaSKUAction
})(BuscaPorSKU);
